import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/auth';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { Modal } from '@components/ui/Modal';
import { PasswordStrengthIndicator } from '@components/auth/PasswordStrengthIndicator';
import { validatePassword } from '@/utils/passwordSecurity';
import { cn } from '@/utils';
import type { Theme } from '@/types';
import { useTheme } from '@/hooks/useTheme';
import { useUIMode } from '@/context/UIModeContext';
import { useGuideTour } from '@/context/GuideTourContext';

import { User, Palette, Bell, Shield, Monitor, Clock, LogOut, ShieldCheck, Mail, Zap, Compass, RotateCcw } from 'lucide-react';

/**
 * IUI Settings Page
 * 
 * Clean settings interface with profile, appearance, and preferences.
 * Follows IUI design principles.
 */
export function SettingsPage() {
  const user = useAuthStore((state) => state.user);
  const updateUser = useAuthStore((state) => state.setUser);

  // Profile state
  const [name, setName] = useState(user?.full_name || user?.name || '');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.full_name || user.name || '');
    }
  }, [user]);

  const { theme, setTheme } = useTheme();

  // Notifications state
  const [emailNotifications, setEmailNotifications] = useState(() =>
    localStorage.getItem('khiimori-notif-email') !== 'false'
  );
  const [appUpdates, setAppUpdates] = useState(() =>
    localStorage.getItem('khiimori-notif-updates') !== 'false'
  );
  const [marketingEmails, setMarketingEmails] = useState(() =>
    localStorage.getItem('khiimori-notif-marketing') === 'true'
  );

  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);

  // Sessions state
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [sessions, setSessions] = useState<any[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const [isRevokingSessions, setIsRevokingSessions] = useState(false);

  const themes: { value: Theme; label: string; description: string }[] = [
    { value: 'light', label: 'Light', description: 'Clean, bright interface' },
    { value: 'dark', label: 'Dark', description: 'Easy on the eyes' },
    { value: 'system', label: 'System', description: 'Follow your device' },
  ];

  const { uiMode, setUIMode } = useUIMode();
  const { startTour } = useGuideTour();
  const uiModes: { value: 'invisible' | 'ambient' | 'explicit'; label: string; description: string }[] = [
    { value: 'explicit', label: 'Explicit', description: 'Full control surfaces' },
    { value: 'ambient', label: 'Ambient', description: 'Subtle presence' },
    { value: 'invisible', label: 'Invisible', description: 'Zero visual footprint' },
  ];

  const handleUIModeChange = (newMode: 'invisible' | 'ambient' | 'explicit') => {
    setUIMode(newMode);
    toast.success(`UI Mode updated to ${newMode}`);
  };

  const handleUpdateProfile = async () => {
    setIsUpdating(true);
    try {
      const updatedUser = await authService.updateUser({ name });
      updateUser(updatedUser);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleThemeChange = (newTheme: Theme) => {
    setTheme(newTheme);
    toast.success(`Theme updated to ${newTheme}`);
  };

  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState('');

  // Email OTP 2FA — see internal/services/otp_service.go. Enabling
  // flips totp_enabled; the next login emails a 6-digit code.
  const [twoFAEnabled, setTwoFAEnabled] = useState(!!user?.totp_enabled);
  const [is2FAEnabling, setIs2FAEnabling] = useState(false);
  const [is2FADisabling, setIs2FADisabling] = useState(false);

  useEffect(() => {
    setTwoFAEnabled(!!user?.totp_enabled);
  }, [user?.totp_enabled]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await authService.getTwoFAStatus();
        if (!cancelled) setTwoFAEnabled(status.enabled);
      } catch {
        // Fall back to user.totp_enabled from the store
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleChangePassword = () => {
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError('');
    setIsChangePasswordOpen(true);
  };

  const handlePasswordSubmit = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    const passwordValidation = validatePassword(passwordData.newPassword);
    if (!passwordValidation.isValid) {
      setPasswordError(passwordValidation.error || 'Password does not meet security requirements');
      return;
    }

    setIsChangingPassword(true);
    setPasswordError('');
    try {
      await authService.changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success('Password changed successfully');
      setIsChangePasswordOpen(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      setPasswordError(error?.response?.data?.message || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleEnable2FA = async () => {
    if (!window.confirm('Enable email verification codes? You will receive a 6-digit code by email each time you sign in.')) return;
    setIs2FAEnabling(true);
    try {
      await authService.enableTwoFA();
      setTwoFAEnabled(true);
      if (user) updateUser({ ...user, totp_enabled: true });
      toast.success('Email verification codes enabled');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to enable 2FA');
    } finally {
      setIs2FAEnabling(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Disable email verification codes? You will no longer need a code to sign in.')) return;
    setIs2FADisabling(true);
    try {
      await authService.disableTwoFA();
      setTwoFAEnabled(false);
      if (user) updateUser({ ...user, totp_enabled: false });
      toast.success('Email verification codes disabled');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to disable 2FA');
    } finally {
      setIs2FADisabling(false);
    }
  };

  const handleViewSessions = async () => {
    setSessionsError(null);
    setIsLoadingSessions(true);
    setShowSessionsModal(true);
    try {
      const data = await authService.getUserSessions();
      setSessions(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setSessionsError(err?.message || 'Failed to load sessions');
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const handleRevokeAllSessions = async () => {
    setIsRevokingSessions(true);
    try {
      await authService.revokeAllSessions();
      setSessions([]);
      toast.success('All other sessions have been revoked');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to revoke sessions');
    } finally {
      setIsRevokingSessions(false);
    }
  };

  const handleDeleteAccount = () => {
    setShowDeleteAccountModal(true);
  };

  const confirmDeleteAccount = async () => {
    setShowDeleteAccountModal(false);
    toast.promise(
      authService.deleteUser().then(() => {
        toast.success('Account deleted');
        useAuthStore.getState().logout();
      }),
      {
        loading: 'Deleting account...',
        success: 'Account deleted',
        error: 'Failed to delete account',
      }
    );
  };

  const sections = [
    {
      id: 'profile',
      title: 'Profile',
      icon: User,
      content: (
        <div className="space-y-4">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
          />
          <Input
            label="Email"
            type="email"
            value={user?.email || ''}
            disabled
            hint="Email cannot be changed"
          />
          <Button onClick={handleUpdateProfile} isLoading={isUpdating}>
            Save Changes
          </Button>
        </div>
      ),
    },
    {
      id: 'appearance',
      title: 'Appearance',
      icon: Palette,
      content: (
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-khiimori-text-primary mb-1">Theme</p>
            <p className="text-sm text-khiimori-text-muted mb-3">Choose your preferred theme</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {themes.map((t) => (
                <button
                  key={t.value}
                  onClick={() => handleThemeChange(t.value)}
                  className={cn(
                    'p-4 rounded-lg border text-left transition-all duration-150',
                    theme === t.value
                      ? 'border-primary-500 bg-primary-500/20'
                      : 'border-khiimori-border/20 hover:border-khiimori-border/30 hover:bg-khiimori-bg-elevated/10'
                  )}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div
                      className={cn(
                        'w-4 h-4 rounded-full border-2',
                        theme === t.value ? 'border-primary-500 bg-primary-500' : 'border-khiimori-border'
                      )}
                    />
                    <span className="text-sm font-medium text-khiimori-text-primary">{t.label}</span>
                  </div>
                  <p className="text-xs text-khiimori-text-muted">{t.description}</p>
                </button>
              ))}
            </div>
          </div>
          
          <div className="pt-4 border-t border-khiimori-border/10">
            <p className="text-sm font-medium text-khiimori-text-primary mb-1">UI Mode</p>
            <p className="text-sm text-khiimori-text-muted mb-3">Choose how visible the interface is</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {uiModes.map((m) => (
                <button
                  key={m.value}
                  onClick={() => handleUIModeChange(m.value)}
                  className={cn(
                    'p-4 rounded-lg border text-left transition-all duration-150',
                    uiMode === m.value
                      ? 'border-primary-500 bg-primary-500/20'
                      : 'border-khiimori-border/20 hover:border-khiimori-border/30 hover:bg-khiimori-bg-elevated/10'
                  )}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div
                      className={cn(
                        'w-4 h-4 rounded-full border-2',
                        uiMode === m.value ? 'border-primary-500 bg-primary-500' : 'border-khiimori-border'
                      )}
                    />
                    <span className="text-sm font-medium text-khiimori-text-primary">{m.label}</span>
                  </div>
                  <p className="text-xs text-khiimori-text-muted">{m.description}</p>
                </button>
              ))}
            </div>
          </div>
          <div className="pt-4 border-t border-khiimori-border/10">
            <p className="text-sm font-medium text-khiimori-text-primary mb-1">Interactive Guide Tour</p>
            <p className="text-sm text-khiimori-text-muted mb-3">Take a live spotlight walkthrough of the application to explore all features, AI hubs, and shortcuts.</p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 rounded-xl bg-khiimori-bg-elevated/20 border border-primary-500/20">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-primary-500/20 text-primary-300">
                  <Compass className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-sm font-medium text-khiimori-text-primary">Interactive Application Tour</div>
                  <div className="text-xs text-khiimori-text-secondary/60">Covers Home, Tabs, AI Hub, App Generators, & Shortcuts</div>
                </div>
              </div>
              <Button
                variant="gradient"
                size="sm"
                onClick={() => startTour(0)}
                leftIcon={<Compass className="w-4 h-4" />}
              >
                Launch Guide Tour
              </Button>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: Bell,
      content: (
        <div className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-khiimori-text-primary">Email Notifications</p>
              <p className="text-xs text-khiimori-text-muted">Receive notifications via email</p>
            </div>
            <button
              role="switch"
              aria-checked={emailNotifications}
              onClick={() => {
                const newValue = !emailNotifications;
                setEmailNotifications(newValue);
                localStorage.setItem('khiimori-notif-email', String(newValue));
              }}
              className={cn(
                'relative w-11 h-6 rounded-full transition-colors duration-200',
                emailNotifications ? 'bg-primary-500' : 'bg-khiimori-bg-tertiary'
              )}
            >
              <span
                className={cn(
                  'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200',
                  emailNotifications && 'translate-x-5'
                )}
              />
            </button>
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-khiimori-text-primary">App Updates</p>
              <p className="text-xs text-khiimori-text-muted">Get notified about new features</p>
            </div>
            <button
              role="switch"
              aria-checked={appUpdates}
              onClick={() => {
                const newValue = !appUpdates;
                setAppUpdates(newValue);
                localStorage.setItem('khiimori-notif-updates', String(newValue));
              }}
              className={cn(
                'relative w-11 h-6 rounded-full transition-colors duration-200',
                appUpdates ? 'bg-primary-500' : 'bg-khiimori-bg-tertiary'
              )}
            >
              <span
                className={cn(
                  'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200',
                  appUpdates && 'translate-x-5'
                )}
              />
            </button>
          </div>

          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-khiimori-text-primary">Marketing Emails</p>
              <p className="text-xs text-khiimori-text-muted">Occasional updates about Khiimori</p>
            </div>
            <button
              role="switch"
              aria-checked={marketingEmails}
              onClick={() => {
                const newValue = !marketingEmails;
                setMarketingEmails(newValue);
                localStorage.setItem('khiimori-notif-marketing', String(newValue));
              }}
              className={cn(
                'relative w-11 h-6 rounded-full transition-colors duration-200',
                marketingEmails ? 'bg-primary-500' : 'bg-khiimori-bg-tertiary'
              )}
            >
              <span
                className={cn(
                  'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200',
                  marketingEmails && 'translate-x-5'
                )}
              />
            </button>
          </div>
        </div>
      ),
    },
    {
      id: 'security',
      title: 'Security',
      icon: Shield,
      content: (
        <div className="space-y-4">
          <Button variant="secondary" className="w-full" onClick={handleChangePassword}>
            Change Password
          </Button>

          <div className="flex items-center justify-between p-3 rounded-lg bg-khiimori-bg-elevated/10 border border-khiimori-border/10">
            <div className="flex items-center gap-3 min-w-0">
              <div className={cn(
                'flex-shrink-0 rounded-full p-2',
                twoFAEnabled ? 'bg-success-500/20' : 'bg-khiimori-bg-elevated/20'
              )}>
                {twoFAEnabled ? (
                  <ShieldCheck className="w-4 h-4 text-success-400" />
                ) : (
                  <Mail className="w-4 h-4 text-khiimori-text-muted" />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-khiimori-text-primary">Two-Factor Authentication</p>
                <p className="text-xs text-khiimori-text-muted">
                  {twoFAEnabled
                    ? 'Enabled — a code is emailed at each sign-in'
                    : 'Get a verification code by email when you sign in'}
                </p>
              </div>
            </div>
            {twoFAEnabled ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDisable2FA}
                isLoading={is2FADisabling}
              >
                Disable
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleEnable2FA}
                isLoading={is2FAEnabling}
              >
                Enable
              </Button>
            )}
          </div>

          <div className="pt-4 border-t border-khiimori-border/10">
            <p className="text-sm font-medium text-khiimori-text-primary mb-2">Active Sessions</p>
            <p className="text-xs text-khiimori-text-muted mb-3">Manage your logged-in devices</p>
            <Button variant="ghost" size="sm" onClick={handleViewSessions}>
              View All Sessions
            </Button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full max-w-full min-h-0 px-4 pt-6 pb-32 sm:px-6 lg:px-8 lg:pt-8">
      <div className="mx-auto w-full max-w-2xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-khiimori-text-primary">Settings</h1>
          <p className="text-khiimori-text-muted mt-1">
            Manage your account preferences and settings
          </p>
        </div>

        {/* Settings Sections */}
        <div className="space-y-6">
          {sections.map((section) => (
            <div
              key={section.id}
              className="bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-xl p-6"
            >
              <div className="mb-4 flex items-center gap-3">
                <div className="rounded-lg bg-khiimori-bg-elevated/10 p-2 text-khiimori-text-secondary">
                  <section.icon />
                </div>
                <h2 className="text-lg font-semibold text-khiimori-text-primary">{section.title}</h2>
              </div>
              {section.content}
            </div>
          ))}

          {/* Danger Zone */}
          <div className="rounded-xl border border-error-500/30 bg-khiimori-bg-elevated/5 p-6 backdrop-blur-xl">
            <h2 className="mb-2 text-lg font-semibold text-error-400">Danger Zone</h2>
            <p className="mb-4 text-sm text-khiimori-text-muted">
              Once you delete your account, there is no going back. Please be certain.
            </p>
            <Button variant="danger" onClick={handleDeleteAccount}>
              Delete Account
            </Button>
          </div>
        </div>

        {/* Change Password Modal */}
        <Modal isOpen={isChangePasswordOpen} onClose={() => setIsChangePasswordOpen(false)} title="Change Password">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-khiimori-text-primary mb-1.5">Current Password</label>
              <Input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                placeholder="Enter current password"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-khiimori-text-primary mb-1.5">New Password</label>
              <Input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                placeholder="Min 8 chars with upper, lower, number, special"
                passwordToggle
              />
              <PasswordStrengthIndicator password={passwordData.newPassword} />
            </div>
            <div>
              <label className="block text-sm font-medium text-khiimori-text-primary mb-1.5">Confirm New Password</label>
              <Input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
                passwordToggle
              />
            </div>
            {passwordError && (
              <p className="text-sm text-error-400">{passwordError}</p>
            )}
            <div className="flex gap-3 pt-2">
              <Button variant="secondary" className="flex-1" onClick={() => setIsChangePasswordOpen(false)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handlePasswordSubmit} isLoading={isChangingPassword}>
                Change Password
              </Button>
            </div>
          </div>
        </Modal>

        {/* Delete Account Confirmation Modal */}
        <Modal
          isOpen={showDeleteAccountModal}
          onClose={() => setShowDeleteAccountModal(false)}
          title="Delete Account"
          description="This action is irreversible. All your data will be permanently deleted. Please be absolutely certain before proceeding."
          size="sm"
        >
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setShowDeleteAccountModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" className="flex-1" onClick={confirmDeleteAccount}>
              Delete My Account
            </Button>
          </div>
        </Modal>

        {/* Sessions Management Modal */}
        <Modal
          isOpen={showSessionsModal}
          onClose={() => setShowSessionsModal(false)}
          title="Active Sessions"
          description="Manage your logged-in devices and sessions"
          size="md"
        >
          <div className="space-y-4">
            {isLoadingSessions && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
              </div>
            )}
            {sessionsError && (
              <p className="text-sm text-error-400">{sessionsError}</p>
            )}
            {!isLoadingSessions && !sessionsError && sessions.length === 0 && (
              <div className="text-center py-8">
                <Monitor className="w-10 h-10 text-khiimori-text-muted mx-auto mb-3" />
                <p className="text-sm text-khiimori-text-muted">No active sessions found</p>
              </div>
            )}
            {!isLoadingSessions && sessions.length > 0 && (
              <>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {sessions.map((session, index) => (
                    <div
                      key={session.id || index}
                      className="flex items-center justify-between p-3 rounded-lg bg-khiimori-bg-elevated/10 border border-khiimori-border/10"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex-shrink-0 rounded-full bg-primary-500/20 p-2">
                          <Monitor className="w-4 h-4 text-primary-400" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-khiimori-text-primary truncate">
                              Session {session.id ? session.id.slice(0, 8) + '...' : `#${index + 1}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-khiimori-text-muted mt-0.5">
                            <Clock className="w-3 h-3" />
                            <span>Created: {session.created_at ? new Date(session.created_at).toLocaleString() : 'Unknown'}</span>
                          </div>
                          {session.expires_at && (
                            <div className="text-xs text-khiimori-text-muted mt-0.5">
                              Expires: {new Date(session.expires_at).toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pt-3 border-t border-khiimori-border/10">
                  <Button
                    variant="danger"
                    className="w-full"
                    onClick={handleRevokeAllSessions}
                    isLoading={isRevokingSessions}
                    leftIcon={<LogOut className="w-4 h-4" />}
                  >
                    Revoke All Other Sessions
                  </Button>
                </div>
              </>
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
}